import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Mail, Cookie } from "lucide-react";
import { SiX } from "react-icons/si";
import { FaLinkedin } from "react-icons/fa";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { SiteNavItem } from "@shared/schema";
import { CookiePreferencesModal } from "@/components/cookie-preferences-modal";
import { useAuth } from "@/hooks/use-auth";

const FALLBACK_QUICK_LINKS = [
  { url: "/",          label: "Home" },
  { url: "/news",      label: "Latest News" },
  { url: "/companies", label: "Companies" },
  { url: "/people",    label: "People" },
];

const FALLBACK_RESOURCES = [
  { url: "/magazine", label: "FM World Magazine" },
  { url: "/videos",   label: "Video Content" },
  { url: "/about",    label: "About Us" },
  { url: "/contact",  label: "Contact" },
];

const FALLBACK_LEGAL = [
  { url: "/page/privacy", label: "Privacy Policy" },
  { url: "/page/terms",   label: "Terms of Service" },
];

function NavLink({ item, className }: { item: Pick<SiteNavItem, "url" | "label" | "openInNewTab">; className?: string }) {
  if (item.openInNewTab) {
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label}
      </a>
    );
  }
  return (
    <Link
      href={item.url}
      className={className}
      data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {item.label}
    </Link>
  );
}


export function SiteFooter() {
  const [showCookiePrefs, setShowCookiePrefs] = useState(false);
  const { isAuthenticated } = useAuth();
  const { data: siteNavItems = [] } = useQuery<SiteNavItem[]>({
    queryKey: ["/api/site-nav"],
    staleTime: 60_000,
  });
  const { data: siteBranding } = useQuery<{ logoUrl: string; siteName: string }>({
    queryKey: ["/api/public/site-branding"],
    staleTime: 5 * 60_000,
  });

  const visibleFor = (placement: SiteNavItem["placement"]) =>
    siteNavItems.filter(i => i.placement === placement && i.isVisible)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  const quickLinks   = visibleFor("footer_quick_links");
  const resources    = visibleFor("footer_resources");
  const legalLinks   = visibleFor("footer_legal");

  const quickLinkItems  = quickLinks.length  > 0 ? quickLinks  : FALLBACK_QUICK_LINKS.map((l, i) => ({ ...l, id: l.url, placement: "footer_quick_links" as const, displayOrder: i, isVisible: true, openInNewTab: false, createdAt: null, updatedAt: null }));
  const resourceItems   = resources.length   > 0 ? resources   : FALLBACK_RESOURCES.map((l, i) => ({ ...l, id: l.url, placement: "footer_resources" as const, displayOrder: i, isVisible: true, openInNewTab: false, createdAt: null, updatedAt: null }));
  const legalItems      = legalLinks.length  > 0 ? legalLinks  : FALLBACK_LEGAL.map((l, i) => ({ ...l, id: l.url, placement: "footer_legal" as const, displayOrder: i, isVisible: true, openInNewTab: false, createdAt: null, updatedAt: null }));

  return (
    <footer className="bg-slate-900 text-slate-300 py-12">
      <div className="mx-auto max-w-7xl px-4">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <div>
            <div className="mb-4">
              {siteBranding?.logoUrl ? (
                <img
                  src={siteBranding.logoUrl}
                  alt={siteBranding.siteName || "FM World"}
                  width={160}
                  height={40}
                  className="h-10 w-auto object-contain brightness-0 invert"
                  data-testid="img-footer-logo"
                />
              ) : (
                <span className="text-white font-bold text-lg" data-testid="img-footer-logo">
                  {siteBranding?.siteName || "FM World"}
                </span>
              )}
            </div>
            <p className="text-sm opacity-80">
              Your comprehensive source for distribution industry intelligence,
              connecting companies, people, and insights.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              {quickLinkItems.map((item, idx) => (
                <li key={item.id ?? idx}>
                  <NavLink item={item} className="hover:text-white transition-colors" />
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Resources</h3>
            <ul className="space-y-2 text-sm">
              {resourceItems.map((item, idx) => (
                <li key={item.id ?? idx}>
                  <NavLink item={item} className="hover:text-white transition-colors" />
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Connect</h3>
            <div className="flex gap-3">
              <Button size="icon" variant="ghost" aria-label="LinkedIn" data-testid="link-linkedin">
                <FaLinkedin className="h-5 w-5" />
              </Button>
              <Button size="icon" variant="ghost" aria-label="X (Twitter)" data-testid="link-twitter">
                <SiX className="h-5 w-5" />
              </Button>
              <Button size="icon" variant="ghost" aria-label="Email" data-testid="link-email">
                <Mail className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-sm mt-4 opacity-80">
              contact@fmworld.com
            </p>
          </div>
        </div>

        <div className="border-t border-slate-700 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm opacity-60">
            2026 FM World. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-6 text-sm opacity-60 items-center">
            {legalItems.map((item, idx) => (
              <NavLink
                key={item.id ?? idx}
                item={item}
                className="hover:opacity-100 transition-opacity"
              />
            ))}
            {!isAuthenticated && (
              <button
                type="button"
                onClick={() => setShowCookiePrefs(true)}
                className="hover:opacity-100 transition-opacity flex items-center gap-1.5"
                data-testid="button-cookie-settings-footer"
              >
                <Cookie className="h-3.5 w-3.5" />
                Cookie Settings
              </button>
            )}
          </div>
        </div>
      </div>

      {showCookiePrefs && <CookiePreferencesModal onClose={() => setShowCookiePrefs(false)} />}
    </footer>
  );
}
